<template>
  <div class="measurement-result-input-table">
    <div class="measurement-result-table">
      <div class="table-row">
        <div class="table-cell label">検査日時</div>
        <div class="table-cell parentheses">
          <span>{{ jaRunDateTime }}</span>
        </div>
      </div>
      <div class="table-row">
        <div class="table-cell label">検査名</div>
        <div class="table-cell">{{ categoryName }}</div>
      </div>
      <div class="table-row">
        <div class="table-cell label">検査機器</div>
        <div class="table-cell instrument">{{ instrumentName }}</div>
      </div>
      <div class="table-row">
        <div class="table-cell label">動物種</div>
        <div class="table-cell">
          <div class="button-wrapper">
            <base-select-box
              data-test="base-select-box"
              :is-value-string="true"
              :selectData="laboratorySpeciesOptions"
              :styles="{ height: '28px', width: '90px' }"
              :value="measurementResult.masterLaboratorySpeciesCode"
              :value-key="'code'"
              @input="
                inputMeasurementResult({
                  key: 'masterLaboratorySpeciesCode',
                  value: $event
                })
              "
            />
            <base-button-small-orange
              data-test="base-button-small-orange"
              :disabled="!measurementResult.masterLaboratorySpeciesCode"
              @click="openUnitReferenceRangePopup"
              >単位基準値一括変換</base-button-small-orange
            >
          </div>
          <hint-balloon
            data-test="hint-balloon"
            :balloon-style="balloon.balloonStyle"
            :cover-style="balloon.coverStyle"
            :note="balloon.note"
            :text-style="balloon.textStyle"
          />
        </div>
      </div>
      <div
        v-if="isNxMeasurementResult"
        class="table-row"
        data-test="specimen-id"
      >
        <div class="table-cell label">No.</div>
        <div class="table-cell">{{ specimenId }}</div>
      </div>
      <div
        v-if="isNxMeasurementResult"
        class="table-row"
        data-test="nx-patient-id"
      >
        <div class="table-cell label">検体ID</div>
        <div class="table-cell">{{ nxPatientId }}</div>
      </div>
    </div>
    <div class="measurement-result-items-table">
      <div class="table-header">
        <div class="table-cell label">検査項目</div>
        <div class="table-cell label center">結果</div>
        <div class="table-cell label center">単位</div>
        <div class="table-cell label center">基準値</div>
      </div>
      <div class="table-body">
        <measurement-result-input-table-row
          v-for="(item, index) in measurementResultItems"
          class="table-body-row"
          data-test="measurement-result-input-table-row"
          :key="item.id"
          :index="index"
          :instrument-code="measurementResult.masterLaboratoryInstrumentCode"
          :measurement-result-item="item"
          @input="inputMeasurementResultItem"
        />
      </div>
    </div>
    <div v-if="showNote" class="note">
      <div class="label">注釈：</div>
      <div class="value">{{ measurementResult.note }}</div>
    </div>
    <unit-reference-range-popup
      v-if="showUnitReferenceRangePopup"
      data-test="unit-reference-range-popup"
      :layer-number="layerNumber + 1"
      :measurement-result="measurementResult"
      :measurement-result-items="measurementResultItems"
      :patient="patient"
      @close="closeUnitReferenceRangePopup"
      @input="inputMeasurementResultItems"
    />
  </div>
</template>

<script>
import BaseButtonSmallOrange from '@/components/parts/atoms/BaseButtonSmallOrange'
import BaseSelectBox from '@/components/parts/atoms/BaseSelectBox'
import HintBalloon from '@/components/parts/atoms/HintBalloon'
import MeasurementResultInputTableRow from '@/components/parts/molecules/MeasurementResultInputTableRow'
import UnitReferenceRangePopup from '@/components/popups/UnitReferenceRangePopup'
import dedent from 'dedent'
import { isNxMeasurementResult } from '@/utils/measurement_utils'
import { mapGetters } from 'vuex'
import { toJaDateTime } from '@/utils/moment'

export default {
  name: 'MeasurementResultInputTable',

  components: {
    BaseButtonSmallOrange,
    BaseSelectBox,
    HintBalloon,
    MeasurementResultInputTableRow,
    UnitReferenceRangePopup
  },

  props: {
    layerNumber: { type: Number, default: 1 },
    measurementResult: { type: Object },
    measurementResultItems: { type: Array },
    nxMeasurementResult: { type: Object },
    patient: { type: Object }
  },

  data() {
    return {
      balloon: {
        balloonStyle: {
          '--left': '500px',
          boxSizing: 'border-box',
          left: '-490px',
          pointerEvents: 'none',
          width: '540px',
          zIndex: '2000'
        }, // テキスト長に応じて調整
        coverStyle: {
          background: 'rgba(120, 120, 120, 0.5)',
          cursor: 'pointer',
          zIndex: '1800'
        },
        note: dedent`
          患者の月齢と検査結果の動物種に応じて単位と基準値の値を一括変換できます。

          一括変換の参照元の値は設定メニューの、
          『検査機器連携/検査項目の単位、基準値、説明文のデフォルト値の設定』
          画面で設定されている単位と基準値の値です。
        `,
        textStyle: {
          fontSize: '14px',
          whiteSpace: 'pre-wrap'
        }
      },
      idexxInstrumentCodes: ['003', '015', '017', '014'],
      showUnitReferenceRangePopup: false
    }
  },

  computed: {
    ...mapGetters({
      getCategory: 'master/getMasterLaboratoryCategoryIndexedByCode',
      getInstrument: 'master/getMasterLaboratoryInstrumentIndexedByCode',
      getMaster: 'master/getData',
      getPatient: 'patients/getDataById',
      getSpecies: 'species/getDataById',
      laboratorySpecies: 'master/getMasterLaboratorySpecies'
    }),
    categoryName() {
      const categoryCode = this.measurementResult.masterLaboratoryCategoryCode
      const category = this.getCategory(categoryCode)
      return category?.name ?? ''
    },
    instrumentName() {
      const instrumentCode = this.measurementResult
        .masterLaboratoryInstrumentCode
      const instrument = this.getInstrument(instrumentCode)
      return instrument?.name ?? ''
    },
    isNxMeasurementResult() {
      return isNxMeasurementResult(this.measurementResult)
    },
    jaRunDateTime() {
      const { date, time } = this.measurementResult
      const YYYYMMDDHHmm = date && time ? `${date}${time}` : ''
      return toJaDateTime(YYYYMMDDHHmm)
    },
    laboratorySpeciesOptions() {
      const masterLaboratorySpecies = this.laboratorySpecies.map(v => ({
        code: v.code,
        name: v.name
      }))
      return [{ code: '', name: '' }].concat(masterLaboratorySpecies)
    },
    nxPatientId() {
      return this.nxMeasurementResult?.nxPatientId ?? ''
    },
    showNote() {
      return this.idexxInstrumentCodes.includes(
        this.measurementResult.masterLaboratoryInstrumentCode
      )
    },
    specimenId() {
      return this.nxMeasurementResult?.specimenId ?? ''
    }
  },

  methods: {
    closeUnitReferenceRangePopup() {
      this.showUnitReferenceRangePopup = false
    },
    inputMeasurementResult({ key, value }) {
      this.$emit('input-measurement-result', { key, value })
    },
    inputMeasurementResultItem({ index, key, value }) {
      this.$emit('input-measurement-result-item', { index, key, value })
    },
    inputMeasurementResultItems(measurementResultItems) {
      this.$emit('input-measurement-result-items', measurementResultItems)
    },
    openUnitReferenceRangePopup() {
      this.showUnitReferenceRangePopup = true
    }
  }
}
</script>

<style lang="scss" scoped>
@mixin table-cell {
  box-sizing: border-box;
  padding: 4px 8px;
  border-right: 1px solid #{$light-grey};
  border-bottom: 1px solid #{$light-grey};
  font-size: 14px;
  overflow-wrap: anywhere;
  line-break: strict;
  display: flex;
  justify-content: left;
  align-items: center;
  height: 100%;
  &.label {
    background-color: #{$very-light-shade-orange};
  }
}

.measurement-result-input-table {
  margin-bottom: 20px;
  > .measurement-result-table {
    box-sizing: border-box;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    border-top: 1px solid #{$light-grey};
    border-left: 1px solid #{$light-grey};
    > .table-row {
      box-sizing: border-box;
      display: grid;
      grid-column: span 1;
      grid-template-columns: 80px auto;
      height: 40px;
      > .table-cell {
        @include table-cell;
        text-align: left;
        &.parentheses {
          font-feature-settings: 'palt';
        }
        &.instrument {
          font-size: 13px;
        }
        > .button-wrapper {
          display: flex;
          align-items: center;
          gap: 0 12px;
          ::v-deep .base-button-small-orange {
            > button {
              width: 150px;
              height: 28px;
              &:active {
                margin: 2px auto 0 auto;
                height: 26px;
              }
              &:disabled {
                &:active {
                  margin: 0 auto 0 auto;
                  height: 28px;
                }
              }
              > span {
                height: auto;
                line-height: 20px;
                font-size: 13px;
                font-weight: normal;
                display: inline;
                vertical-align: baseline;
              }
            }
          }
        }
      }
    }
  }
  > .measurement-result-items-table {
    box-sizing: border-box;
    display: grid;
    grid-template-columns: 230px auto 160px 280px;
    border-left: 1px solid #{$light-grey};
    > .table-header {
      grid-column: span 4;
      display: grid;
      grid-template-columns: subgrid;
      height: 36px;
      > .table-cell {
        @include table-cell;
        &.center {
          justify-content: center;
        }
      }
    }
    > .table-body {
      grid-template-columns: subgrid;
      display: grid;
      grid-column-start: 1;
      grid-column-end: 5;
      > .table-body-row {
        &:nth-child(even) {
          background-color: #f7f7f7;
        }
      }
    }
  }
  > .note {
    box-sizing: border-box;
    min-height: 50px;
    white-space: pre-wrap;
    border-left: 1px solid #{$light-grey};
    border-right: 1px solid #{$light-grey};
    border-bottom: 1px solid #{$light-grey};
    padding: 8px;
    > .label {
      font-size: 13px;
      font-weight: bold;
    }
    > .value {
      font-size: 13px;
    }
  }
}
</style>
